// audit-safety-integrated
import { readJson, safeErrorMessage } from '../utils/auditSafety';
import React, { useState, useEffect } from 'react';
import { Search, Download, Eye, X, Landmark, CreditCard, Hash, Building2, MapPin, Phone, User, BadgeInfo } from 'lucide-react';
import * as XLSX from 'xlsx';
import { API_BASE_URL } from '../services/api';

const NA = '—';
const v = (val) => (val && String(val).trim() !== '' ? val : NA);

const StoreDetails = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [viewingStore, setViewingStore] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {

      const res = await fetch(`${API_BASE_URL}/user/banches`, {
        headers: {  'Content-Type': 'application/json' },
      });
      const result = await readJson(res);
      if (!result.data || !Array.isArray(result.data)) { setRows([]); return; }

      const list = result.data.map((item, i) => ({
        _key: `${item.branch_type}__${item.branch}__${i}`,
        type: v(item.branch_type),
        unitName: v(item.branch),
        location: v(item.location),
        storeName: v(item.store),
        storeContactName: v(item['store manager']),
        contact: v(item.contact),
        address: v(item.address),
        district: v(item.district),
        storeName2: v(item['store name']),
        storeAddress: v(item['store address']),
        storeContact: v(item['store contact']),
        storeDistrict: v(item['store district']),
        bankName: v(item.store_bank?.bank),
        accountNo: v(item.store_bank?.['a/c number']),
        ifsc: v(item.store_bank?.ifsc),
        bankBranch: v(item.store_bank?.bank_branch),
        accountHolder: v(item.store_bank?.['a/c holder']),
      }));

      setRows(list);
    } catch (err) {
      setError(safeErrorMessage(err) || 'Network error');
    } finally {
      setLoading(false);
    }
  };

  const filtered = rows.filter((r) => {
    const q = search.toLowerCase();
    return (
      r.type.toLowerCase().includes(q) ||
      r.unitName.toLowerCase().includes(q) ||
      r.storeName.toLowerCase().includes(q) ||
      r.district.toLowerCase().includes(q) ||
      r.location.toLowerCase().includes(q) ||
      r.contact.toLowerCase().includes(q) ||
      r.storeContactName.toLowerCase().includes(q)
    );
  });

  const exportExcel = () => {
    const data = filtered.map((r, i) => ({
      'S.No': i + 1,
      'Type': r.type,
      'Unit Name': r.unitName,
      'District': r.district,
      'Location': r.location,
      'Store Code': r.storeName,
      'Store Name': r.storeName2,
      'Store Manager': r.storeContactName,
      'Branch Contact No.': r.contact,
      'Store Contact No.': r.storeContact,
      'Branch Address': r.address,
      'Store Address': r.storeAddress,
      'Store District': r.storeDistrict,
      'Bank Name': r.bankName,
      'Account No': r.accountNo,
      'IFSC Code': r.ifsc,
      'Bank Branch': r.bankBranch,
      'Account Holder': r.accountHolder,
    }));

    const ws = XLSX.utils.json_to_sheet(data);
    const colWidths = Object.keys(data[0] || {}).map((k) => ({
      wch: Math.min(
        Math.max(k.length, ...data.map((r) => String(r[k] ?? '').length)) + 2,
        40
      ),
    }));
    ws['!cols'] = colWidths;

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Store Details');
    XLSX.writeFile(wb, 'store_details.xlsx');
  };

  const hasBanking = (r) =>
    [r.bankName, r.accountNo, r.ifsc, r.bankBranch, r.accountHolder].some((f) => f !== NA);

  // ── Sub-components ───────────────────────────────────────────

  const InfoRow = ({ icon: Icon, label, value, mono, span }) => (
    <div className={span ? 'col-span-2' : ''}>
      <p className="text-xs text-gray-400 uppercase tracking-wide mb-0.5">{label}</p>
      <div className="flex items-start gap-1.5">
        {Icon && <Icon size={13} className="text-gray-400 mt-0.5 shrink-0" />}
        <p className={`text-sm text-gray-800 font-medium break-words leading-snug ${mono ? 'font-mono' : ''}`}>
          {value}
        </p>
      </div>
    </div>
  );

  const BankBadge = ({ has }) =>
    has ? (
      <span className="inline-flex items-center gap-1 text-xs font-medium bg-green-100 text-green-700 px-2 py-0.5 rounded-full">
        <Landmark size={11} /> Available
      </span>
    ) : (
      <span className="inline-flex items-center gap-1 text-xs font-medium bg-gray-100 text-gray-400 px-2 py-0.5 rounded-full">
        <Landmark size={11} /> {NA}
      </span>
    );

  if (loading)
    return (
      <div className="flex items-center justify-center min-h-screen bg-slate-50">
        <div className="text-slate-500 text-sm animate-pulse">Loading store details…</div>
      </div>
    );

  if (error)
    return (
      <div className="flex items-center justify-center min-h-screen bg-slate-50">
        <div className="text-red-500 font-medium">{error}</div>
      </div>
    );

  return (
    <div className="min-h-screen bg-slate-50 py-8">
      <div className="max-w-7xl mx-auto px-4">

        {/* Header */}
        <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold text-gray-800">Store Details</h1>
            <p className="text-sm text-gray-500 mt-1">
              {filtered.length} stores&nbsp;·&nbsp;Profile &amp; banking information
            </p>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            <div className="relative">
              <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search stores…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9 pr-4 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-300 bg-white w-56"
              />
            </div>
            <button
              onClick={exportExcel}
              className="flex items-center gap-2 px-4 py-2 text-sm bg-green-600 hover:bg-green-700 text-white rounded-lg transition"
            >
              <Download size={15} /> Export Excel
            </button>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl shadow border border-slate-200 overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-100 text-slate-600 uppercase text-xs">
              <tr>
                <th className="px-4 py-3 text-left w-10">S.No</th>
                <th className="px-4 py-3 text-left">Type</th>
                <th className="px-4 py-3 text-left">Unit Name</th>
                <th className="px-4 py-3 text-left">District</th>
                <th className="px-4 py-3 text-left">Location</th>
                <th className="px-4 py-3 text-left">Store Name</th>
                <th className="px-4 py-3 text-left">Store Manager</th>
                <th className="px-4 py-3 text-left">Contact No.</th>
                <th className="px-4 py-3 text-center">Banking</th>
                <th className="px-4 py-3 text-center">View</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={10} className="px-4 py-12 text-center text-gray-400">
                    No stores found.
                  </td>
                </tr>
              )}
              {filtered.map((r, i) => (
                <tr key={r._key} className="hover:bg-slate-50 transition">
                  <td className="px-4 py-3 text-slate-400 text-xs">{i + 1}</td>
                  <td className="px-4 py-3">
                    <span className="inline-flex rounded-full bg-indigo-100 px-3 py-1 text-xs font-semibold text-indigo-700 whitespace-nowrap">
                      {r.type}
                    </span>
                  </td>
                  <td className="px-4 py-3 font-semibold text-slate-800 whitespace-nowrap">{r.unitName}</td>
                  <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{r.district}</td>
                  <td className="px-4 py-3 text-slate-600 max-w-[160px] truncate" title={r.location}>
                    {r.location}
                  </td>
                  <td className="px-4 py-3">
                    <span className="font-mono text-xs text-slate-700 bg-slate-100 px-2 py-0.5 rounded">
                      {r.storeName2 !== NA ? r.storeName2 : r.storeName}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-slate-700">{r.storeContactName}</td>
                  <td className="px-4 py-3 text-slate-700 whitespace-nowrap">{r.contact}</td>
                  <td className="px-4 py-3 text-center">
                    <BankBadge has={hasBanking(r)} />
                  </td>
                  <td className="px-4 py-3 text-center">
                    <button
                      onClick={() => setViewingStore(r)}
                      className="inline-flex items-center justify-center rounded-full p-1.5 text-indigo-500 hover:bg-indigo-100 transition"
                      title="View full details"
                    >
                      <Eye size={15} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Detail Modal */}
      {viewingStore && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm px-4">
          <div className="relative w-full max-w-2xl rounded-2xl bg-white shadow-2xl max-h-[90vh] flex flex-col">

            {/* Modal Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b bg-indigo-600 rounded-t-2xl">
              <div>
                <h3 className="text-lg font-bold text-white">{viewingStore.unitName}</h3>
                <p className="text-indigo-200 text-xs mt-0.5">{viewingStore.type} &nbsp;·&nbsp; {viewingStore.district}</p>
              </div>
              <button
                onClick={() => setViewingStore(null)}
                className="text-white/70 hover:text-white transition p-1"
              >
                <X size={20} />
              </button>
            </div>

            <div className="overflow-y-auto flex-1 px-6 py-5 space-y-6">

              {/* Branch Profile Section */}
              <div>
                <p className="text-xs font-bold text-gray-500 uppercase tracking-widest mb-3 flex items-center gap-2">
                  <BadgeInfo size={13} /> Branch Details
                </p>
                <div className="grid grid-cols-2 gap-4">
                  <InfoRow icon={Building2} label="Type" value={viewingStore.type} />
                  <InfoRow icon={MapPin} label="Unit Name" value={viewingStore.unitName} />
                  <InfoRow icon={MapPin} label="District" value={viewingStore.district} />
                  <InfoRow icon={MapPin} label="Location" value={viewingStore.location} />
                  <InfoRow icon={Phone} label="Contact Number" value={viewingStore.contact} />
                  <InfoRow icon={MapPin} label="Address" value={viewingStore.address} span />
                </div>
              </div>

              {/* Store Info Section */}
              <div>
                <p className="text-xs font-bold text-gray-500 uppercase tracking-widest mb-3 flex items-center gap-2">
                  <Building2 size={13} /> Store Details
                </p>
                <div className="bg-slate-50 border border-slate-100 rounded-xl px-4 py-4 grid grid-cols-2 gap-4">
                  <InfoRow icon={BadgeInfo} label="Store Code" value={viewingStore.storeName} mono />
                  <InfoRow icon={Building2} label="Store Name" value={viewingStore.storeName2} span />
                  <InfoRow icon={User} label="Store Manager" value={viewingStore.storeContactName} />
                  <InfoRow icon={Phone} label="Store Contact" value={viewingStore.storeContact} />
                  <InfoRow icon={MapPin} label="Store District" value={viewingStore.storeDistrict} />
                  <InfoRow icon={MapPin} label="Store Address" value={viewingStore.storeAddress} span />
                </div>
              </div>

              {/* Banking Section */}
              <div>
                <p className="text-xs font-bold text-gray-500 uppercase tracking-widest mb-3 flex items-center gap-2">
                  <Landmark size={13} /> Banking Details
                </p>
                <div className="bg-indigo-50 border border-indigo-100 rounded-xl px-4 py-4 grid grid-cols-2 gap-4">
                  <InfoRow icon={Landmark} label="Bank Name" value={viewingStore.bankName} />
                  <InfoRow icon={Hash} label="Account No." value={viewingStore.accountNo} mono />
                  <InfoRow icon={CreditCard} label="IFSC Code" value={viewingStore.ifsc} mono />
                  <InfoRow icon={Building2} label="Bank Branch" value={viewingStore.bankBranch} />
                  <InfoRow icon={User} label="Account Holder" value={viewingStore.accountHolder} span />
                </div>
              </div>

            </div>

            <div className="flex justify-end px-6 py-4 border-t bg-gray-50 rounded-b-2xl">
              <button
                onClick={() => setViewingStore(null)}
                className="px-5 py-2 text-sm font-medium bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StoreDetails;